<div class="copy-modal">
  <header class="modal-header">
    <h2 class="modal-title">
      <ng-container *ngIf="data.sourceProjectName; else fallbackTitle">
        Copy <span class="modal-title-emphasis">{{ data.sourceProjectName }}</span> Information To
      </ng-container>
      <ng-template #fallbackTitle>Copy Resource Data</ng-template>
    </h2>
    <button mat-icon-button (click)="cancel()" aria-label="Close" [disabled]="copying">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <section class="modal-body">

    <!-- Source resource + project context (read-only labels) -->
    <div class="source-row">
      <div class="kv">
        <span class="kv-label">Copy From</span>
        <span class="kv-value">{{ data.sourceResourceName || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-label">Project Name</span>
        <span class="kv-value">
          {{ data.sourceProjectName || '—' }}
          <span class="kv-sub" *ngIf="data.sourceProjectIdCode">{{ data.sourceProjectIdCode }}</span>
        </span>
      </div>
    </div>

    <!-- Copy mode + target picker. Single mode: one target replaces.
         Multiple mode: chip set below the input lets the operator
         queue many targets before submit, matching the Figma. -->
    <div class="target-row">
      <mat-form-field appearance="outline" floatLabel="always" class="form-field">
        <mat-label>Copy Information To</mat-label>
        <mat-select [(value)]="copyMode" (selectionChange)="onModeChange()">
          <mat-option *ngFor="let m of copyModes" [value]="m.value" [disabled]="m.disabled">
            {{ m.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <div class="target-field-wrap">
        <mat-form-field appearance="outline" floatLabel="always" class="form-field">
          <mat-label>Resource Name</mat-label>
          <input
            matInput
            required
            type="text"
            placeholder="Search by name, email, employee ID…"
            [(ngModel)]="searchText"
            (ngModelChange)="onSearchChange()"
            [disabled]="copying"
          />
          <mat-spinner *ngIf="searching" matSuffix diameter="18"></mat-spinner>
          <mat-icon matSuffix *ngIf="!searching">search</mat-icon>
        </mat-form-field>

        <!-- Selected target chips — only rendered in Multi mode; in
             Single mode the picked name lives in the input and a chip
             would be redundant clutter. -->
        <div *ngIf="copyMode === 'multiple' && selectedTargets.length > 0" class="selected-chips">
          <span *ngFor="let t of selectedTargets" class="chip">
            <span class="chip-label">{{ t.resourceName }}</span>
            <button
              type="button"
              class="chip-remove"
              (click)="removeTarget(t)"
              [disabled]="copying"
              [attr.aria-label]="'Remove ' + t.resourceName"
            >
              <mat-icon>close</mat-icon>
            </button>
          </span>
        </div>

        <!-- Inline dropdown of matches anchored to the field-wrap so
             it overlays directly under the input. Suppressed entirely
             when a Single-mode target is already picked — without
             that the lingering searchText (set to the picked name)
             would re-trigger the empty "No matching resources" state. -->
        <div
          *ngIf="!(copyMode === 'single' && selectedTargets.length > 0)
                && (searchResults.length > 0
                    || (searching && searchText.trim().length > 0)
                    || (!searching && searchText.trim().length > 0 && searchResults.length === 0))"
          class="search-results"
        >
          <div *ngIf="searching && searchResults.length === 0" class="search-loading">Searching…</div>
          <button
            *ngFor="let r of searchResults"
            type="button"
            class="search-result"
            [disabled]="!canPick(r)"
            (click)="pickTarget(r)"
          >
            <mat-icon class="result-icon">person_outline</mat-icon>
            <div class="result-meta">
              <span class="result-name">
                {{ r.resourceName }}
                <!-- New-hire rows (no FinanceResource provisioned yet)
                     are still valid copy targets — the BE provisions
                     one on the fly. We just tag them so the operator
                     knows what's about to happen. -->
                <span *ngIf="!r.resourceId" class="result-badge">New hire</span>
              </span>
              <span class="result-id">
                {{ r.employeeId }}
                <span *ngIf="r.email" class="result-sub">· {{ r.email }}</span>
              </span>
            </div>
          </button>
          <div
            *ngIf="!searching && searchText.trim().length > 0 && searchResults.length === 0"
            class="search-empty"
          >
            No matching resources.
          </div>
        </div>
      </div>
    </div>

    <!-- Field preview grid — Figma-faithful: 2-col grid of (checkbox +
         Material outlined readonly field). Same readonly grey treatment
         we use elsewhere in the Finance pages for derived values. -->
    <div class="field-preview" *ngIf="data.fieldPreview && data.fieldPreview.length > 0">
      <p class="field-preview-hint">Please deselect any fields that should not be copied.</p>

      <div class="field-grid">
        <div
          *ngFor="let f of data.fieldPreview"
          class="field-cell"
          [class.unchecked]="!fieldSelections[f.label]"
        >
          <div class="checkbox-cell">
            <mat-checkbox
              [(ngModel)]="fieldSelections[f.label]"
              [disabled]="copying"
              [attr.aria-label]="'Include ' + f.label + ' in copy'"
            ></mat-checkbox>
          </div>

          <mat-form-field
            appearance="outline"
            floatLabel="always"
            class="form-field readonly-field"
          >
            <mat-label>{{ f.label }}</mat-label>
            <!-- Long values truncate with ellipsis; matTooltip surfaces
                 the full text on hover. The native title attribute is a
                 fallback for keyboard / accessibility tooling. -->
            <input
              matInput
              readonly
              [value]="f.value || ''"
              [matTooltip]="f.value || ''"
              [matTooltipDisabled]="!f.value || f.value.length < 24"
              matTooltipShowDelay="200"
              [attr.title]="f.value || null"
            />
          </mat-form-field>
        </div>
      </div>
    </div>
  </section>

  <footer class="modal-footer">
    <button mat-stroked-button type="button" (click)="cancel()" [disabled]="copying">Cancel</button>
    <button
      mat-flat-button
      type="button"
      class="primary-btn"
      (click)="confirmCopy()"
      [disabled]="selectedTargets.length === 0 || copying"
    >
      {{ copying ? 'Copying…' : 'Copy' }}
    </button>
  </footer>
</div>
